import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, formatDateBR } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, RefreshCw, Search, Eye, Copy, CheckCircle, Clock, Check, AlertTriangle, Truck, Package, MapPin, X, Clipboard } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { colors } from '../theme';
import ModalConfirmacaoEntrega from './ModalConfirmacaoEntrega';

// Status que automaticamente vão para "Revisar Hoje"
const STATUS_AUTO_REVISAR = ['EM ROTA DE ENTREGA', 'SAIU PARA ENTREGA'];

const ConferenciaDiaria = ({ user }) => {
  const location = useLocation();
  const [pedidos, setPedidos] = useState([]);
  const [pedidosFiltrados, setPedidosFiltrados] = useState([]);
  const [transportadoras, setTransportadoras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroConferencia, setFiltroConferencia] = useState('PENDENTES'); // PENDENTES, CONFERIDOS, REVISAR
  const [filtroTransportadora, setFiltroTransportadora] = useState('TODAS');
  const [conferindoId, setConferindoId] = useState(null);
  const [statusList, setStatusList] = useState([]);
  const [modalEntrega, setModalEntrega] = useState({ isOpen: false, pedido: null });
  const [modalDetalhes, setModalDetalhes] = useState({ isOpen: false, pedido: null });
  const [modalRevisar, setModalRevisar] = useState({ isOpen: false, pedidoId: null, novoStatus: null });
  const [rastreamentoModal, setRastreamentoModal] = useState({ isOpen: false, pedido: null, dados: null, loading: false });
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [copiado, setCopiado] = useState(null);
  const [salvandoId, setSalvandoId] = useState(null);
  const intervalRef = useRef(null);
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(50);
  
  // Refs para manter valores atualizados no polling
  const buscaRef = useRef(busca);
  const filtroConferenciaRef = useRef(filtroConferencia);
  const filtroTransportadoraRef = useRef(filtroTransportadora);
  
  // Verificar se veio filtro do Dashboard
  useEffect(() => {
    if (location.state?.filtroPedido) {
      setBusca(location.state.filtroPedido);
      setFiltroConferencia('PENDENTES');
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Atualizar refs sempre que os states mudarem
  useEffect(() => {
    buscaRef.current = busca;
  }, [busca]);
  
  useEffect(() => {
    filtroConferenciaRef.current = filtroConferencia;
  }, [filtroConferencia]);
  
  useEffect(() => {
    filtroTransportadoraRef.current = filtroTransportadora;
  }, [filtroTransportadora]);
  
  // Reaplicar filtros quando busca, conferência ou transportadora mudarem
  useEffect(() => {
    aplicarFiltrosAosDados(pedidos, busca, filtroConferencia, filtroTransportadora);
    setPaginaAtual(1);
  }, [busca, filtroConferencia, filtroTransportadora, pedidos]);

  // Polling de 5 segundos
  useEffect(() => {
    console.log('🔄 Conferência Diária - Iniciando atualização automática a cada 5 segundos');
    
    loadPedidos();
    loadTransportadoras();
    loadStatus();
    
    intervalRef.current = setInterval(() => {
      console.log('🔄 Conferência Diária - Atualizando automaticamente');
      loadPedidos(false);
    }, 15000); // 15 segundos - atualização silenciosa
    
    return () => {
      if (intervalRef.current) {
        console.log('🛑 Conferência Diária - Parando atualização automática');
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Helpers para verificar status de conferência
  const isConferidoHoje = (pedido) => {
    if (!pedido.data_conferencia) return false;
    const hoje = new Date().toISOString().split('T')[0];
    const dataConf = pedido.data_conferencia.split('T')[0];
    return dataConf === hoje;
  };

  const isRevisarHoje = (pedido) => {
    return isConferidoHoje(pedido) && pedido.revisar_hoje === true;
  };

  const loadPedidos = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('primaza_token');
      // Usar endpoint otimizado que já filtra no backend
      const response = await axios.get(`${API}/pedidos/conferencia-diaria`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPedidos(response.data);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const loadTransportadoras = async () => {
    try {
      const response = await axios.get(`${API}/transportadoras`);
      setTransportadoras(response.data);
    } catch (error) {
      console.error('Erro ao carregar transportadoras:', error);
    }
  };

  const loadStatus = async () => {
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(`${API}/status-pedido`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const statusAtivos = response.data.filter(s => s.ativo);
      setStatusList(statusAtivos);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
      setStatusList([
        { id: '1', nome: 'FATURADO', cor: '#3B82F6', ordem: 1 },
        { id: '2', nome: 'IMPRESSO', cor: '#8B5CF6', ordem: 2 },
        { id: '3', nome: 'POSTADO', cor: '#10B981', ordem: 5 },
        { id: '4', nome: 'ENTREGUE', cor: '#22C55E', ordem: 10, is_final: true }
      ]);
    }
  };

  // Função auxiliar que aplica filtros em um array de pedidos específico
  const aplicarFiltrosAosDados = (dadosPedidos, termoBusca, filtroConf, transportadora) => {
    let resultado = [...dadosPedidos];

    // Filtro de busca
    if (termoBusca.trim()) {
      const termoLower = termoBusca.toLowerCase();
      resultado = resultado.filter(p => 
        (p.pedido && p.pedido.toLowerCase().includes(termoLower)) ||
        (p.nome_cliente && p.nome_cliente.toLowerCase().includes(termoLower)) ||
        (p.vendedor && p.vendedor.toLowerCase().includes(termoLower)) ||
        (p.transportadora && p.transportadora.toLowerCase().includes(termoLower))
      );
    }

    // Filtro por conferência
    if (filtroConf === 'PENDENTES') {
      resultado = resultado.filter(p => !isConferidoHoje(p));
    } else if (filtroConf === 'CONFERIDOS') {
      resultado = resultado.filter(p => isConferidoHoje(p) && !p.revisar_hoje);
    } else if (filtroConf === 'REVISAR') {
      resultado = resultado.filter(p => isRevisarHoje(p));
    }

    // Filtro por transportadora
    if (transportadora !== 'TODAS') {
      resultado = resultado.filter(p => p.transportadora === transportadora);
    }

    // Ordenar por número do pedido (maior primeiro - mais recente)
    resultado.sort((a, b) => {
      const numA = parseInt(a.pedido) || 0;
      const numB = parseInt(b.pedido) || 0;
      return numB - numA;
    });

    setPedidosFiltrados(resultado);
  };
  
  // Calcular pedidos da página atual
  const indexUltimo = paginaAtual * itensPorPagina;
  const indexPrimeiro = indexUltimo - itensPorPagina;
  const pedidosPaginaAtual = pedidosFiltrados.slice(indexPrimeiro, indexUltimo);
  const totalPaginas = Math.ceil(pedidosFiltrados.length / itensPorPagina);
  
  const irParaPagina = (numeroPagina) => {
    setPaginaAtual(numeroPagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const calcularDiasParados = (created_at) => {
    if (!created_at) return 0;
    const dataCriacao = new Date(created_at);
    const hoje = new Date();
    const diff = hoje - dataCriacao;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const getCorDias = (dias) => {
    if (dias >= 5) return 'bg-red-100 text-red-800 border-red-300';
    if (dias >= 3) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-green-100 text-green-800 border-green-300';
  };

  // Funções de rastreamento Correios
  const buscarRastreamento = async (pedido) => {
    if (!pedido.cod_rastreamento) {
      alert('Este pedido não possui código de rastreamento');
      return;
    }
    
    setRastreamentoModal({ isOpen: true, pedido, dados: null, loading: true });
    
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.get(
        `${API}/correios/rastrear/${pedido.cod_rastreamento}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRastreamentoModal(prev => ({ ...prev, dados: response.data, loading: false }));
    } catch (error) {
      console.error('Erro ao buscar rastreamento:', error);
      setRastreamentoModal(prev => ({ 
        ...prev, 
        dados: { erro: error.response?.data?.detail || 'Erro ao buscar rastreamento' }, 
        loading: false 
      }));
    }
  };

  const abrirModalConfirmacaoEntrega = () => {
    if (!rastreamentoModal.pedido || !rastreamentoModal.dados) return;
    
    let dataEntrega = '';
    if (rastreamentoModal.dados.ultima_data) {
      try {
        const dataISO = rastreamentoModal.dados.ultima_data;
        dataEntrega = dataISO.split('T')[0];
      } catch (e) {
        console.error('Erro ao converter data:', e);
      }
    }
    
    setRastreamentoModal({ isOpen: false, pedido: null, dados: null, loading: false });
    setModalEntrega({ 
      isOpen: true, 
      pedido: rastreamentoModal.pedido, 
      dataPreenchida: dataEntrega 
    });
  };

  const devesugerirAtualizacao = () => {
    if (!rastreamentoModal.dados || !rastreamentoModal.pedido) return null;
    
    const statusPedido = rastreamentoModal.pedido.status_pedido || '';
    const entregue = rastreamentoModal.dados.entregue;
    
    if (entregue && statusPedido !== 'ENTREGUE') {
      return {
        sugerir: true,
        statusSugerido: 'ENTREGUE',
        mensagem: 'Os Correios confirmam: "Objeto entregue ao destinatário"',
        cor: 'green'
      };
    }
    
    return null;
  };

  const copiarLinkPublico = async () => {
    if (!rastreamentoModal.pedido?.cod_rastreamento) return;
    
    const codigo = rastreamentoModal.pedido.cod_rastreamento.replace(/\s/g, '');
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const linkPublico = `${baseUrl}/rastreio/${codigo}`;
    
    try {
      await navigator.clipboard.writeText(linkPublico);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 3000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = linkPublico;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 3000);
    }
  };

  const handleConferir = async (pedidoId, revisarHoje = false) => {
    setConferindoId(pedidoId);
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.post(`${API}/pedidos/${pedidoId}/conferir`, 
        { revisar_hoje: revisarHoje },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Atualizar localmente
      setPedidos(prev => prev.map(p => 
        p.id === pedidoId 
          ? { ...p, data_conferencia: new Date().toISOString(), revisar_hoje: revisarHoje }
          : p
      ));
    } catch (error) {
      console.error('Erro ao conferir pedido:', error);
      alert('Erro ao conferir pedido');
    } finally {
      setConferindoId(null);
    }
  };

  const handleDesconferir = async (pedidoId) => {
    setConferindoId(pedidoId);
    try {
      const token = localStorage.getItem('primaza_token');
      await axios.post(`${API}/pedidos/${pedidoId}/desconferir`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Atualizar localmente
      setPedidos(prev => prev.map(p => 
        p.id === pedidoId 
          ? { ...p, data_conferencia: null, revisar_hoje: false }
          : p
      ));
    } catch (error) {
      console.error('Erro ao desconferir pedido:', error);
      alert('Erro ao desconferir pedido');
    } finally {
      setConferindoId(null);
    }
  };

  const handleStatusChange = async (pedidoId, novoStatus) => {
    const statusObj = statusList.find(s => s.nome === novoStatus);
    
    if (statusObj && statusObj.is_final && novoStatus === 'ENTREGUE') {
      const pedido = pedidos.find(p => p.id === pedidoId);
      setModalEntrega({ isOpen: true, pedido });
      return;
    }

    // Verificar se é status que vai automaticamente para "Revisar Hoje"
    const isStatusAutoRevisar = STATUS_AUTO_REVISAR.includes(novoStatus.toUpperCase());
    
    if (isStatusAutoRevisar) {
      // Status de entrega próxima - marca automaticamente para revisar
      await atualizarStatusEConferir(pedidoId, novoStatus, true);
    } else {
      // Outros status - pergunta se quer revisar
      setModalRevisar({ isOpen: true, pedidoId, novoStatus });
    }
  };

  const atualizarStatusEConferir = async (pedidoId, novoStatus, revisarHoje) => {
    setSalvandoId(pedidoId);
    try {
      const token = localStorage.getItem('primaza_token');
      
      // Atualizar status
      await axios.put(`${API}/pedidos/${pedidoId}`, {
        status_pedido: novoStatus
      });
      
      // Marcar como conferido com flag de revisão
      await axios.post(`${API}/pedidos/${pedidoId}/conferir`, 
        { revisar_hoje: revisarHoje },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Atualizar localmente
      setPedidos(pedidos.map(p => 
        p.id === pedidoId 
          ? { ...p, status_pedido: novoStatus, data_conferencia: new Date().toISOString(), revisar_hoje: revisarHoje } 
          : p
      ));
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    } finally {
      setSalvandoId(null);
    }
  };

  const handleConfirmarRevisao = async (revisarHoje) => {
    const { pedidoId, novoStatus } = modalRevisar;
    setModalRevisar({ isOpen: false, pedidoId: null, novoStatus: null });
    await atualizarStatusEConferir(pedidoId, novoStatus, revisarHoje);
  };

  const getStatusStyle = (statusNome) => {
    const statusObj = statusList.find(s => s.nome === statusNome);
    if (!statusObj) return {};
    
    return {
      backgroundColor: statusObj.cor + '20',
      color: statusObj.cor,
      borderColor: statusObj.cor
    };
  };

  const calcularEstatisticas = () => {
    const pendentes = pedidos.filter(p => !isConferidoHoje(p)).length;
    const conferidos = pedidos.filter(p => isConferidoHoje(p) && !p.revisar_hoje).length;
    const revisar = pedidos.filter(p => isRevisarHoje(p)).length;
    
    return { pendentes, conferidos, revisar };
  };

  const abrirDetalhes = (pedido) => {
    setModalDetalhes({ isOpen: true, pedido });
  };

  const fecharDetalhes = () => {
    setModalDetalhes({ isOpen: false, pedido: null });
    setCopiado(null);
  };

  const copiarCampo = (campo, valor) => {
    navigator.clipboard.writeText(valor || '');
    setCopiado(campo);
    setTimeout(() => setCopiado(null), 2000);
  };

  const copiarTudo = (pedido) => {
    const texto = `PEDIDO: ${pedido.pedido || '-'}
CLIENTE: ${pedido.nome_cliente || '-'}
VENDEDOR: ${pedido.vendedor || '-'}
TRANSPORTADORA: ${pedido.transportadora || '-'}
STATUS: ${pedido.status_pedido || '-'}
NF-e: ${pedido.nfe || '-'}
COD. RASTREAMENTO: ${pedido.cod_rastreamento || '-'}
PREVISÃO ENTREGA: ${formatDateBR(pedido.prev_entrega)}`;
    
    navigator.clipboard.writeText(texto);
    setCopiado('tudo');
    setTimeout(() => setCopiado(null), 2000);
  };

  const hoje = new Date().toLocaleDateString('pt-BR');
  const stats = calcularEstatisticas();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
              📅 Conferência Diária
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {hoje} - Marque os pedidos que você já conferiu hoje
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadPedidos()}
            disabled={loading}
            style={{ 
              borderColor: colors.primaza.primary,
              color: colors.primaza.secondary 
            }}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="max-w-full mx-auto p-6 space-y-6">
        {/* Cards de Filtros por Conferência */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card Pendentes */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filtroConferencia === 'PENDENTES' ? 'ring-2 ring-orange-500 bg-orange-50' : ''}`}
            onClick={() => setFiltroConferencia('PENDENTES')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Não Conferidos</p>
                  <p className="text-3xl font-bold text-orange-600 mt-2">{stats.pendentes}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
              {filtroConferencia === 'PENDENTES' && (
                <p className="text-xs text-orange-600 mt-2">✓ Filtro ativo</p>
              )}
            </CardContent>
          </Card>

          {/* Card Conferidos Hoje */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filtroConferencia === 'CONFERIDOS' ? 'ring-2 ring-green-500 bg-green-50' : ''}`}
            onClick={() => setFiltroConferencia('CONFERIDOS')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Conferidos Hoje</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{stats.conferidos}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
              {filtroConferencia === 'CONFERIDOS' && (
                <p className="text-xs text-green-600 mt-2">✓ Filtro ativo</p>
              )}
            </CardContent>
          </Card>

          {/* Card Revisar Hoje */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filtroConferencia === 'REVISAR' ? 'ring-2 ring-amber-500 bg-amber-50' : ''}`}
            onClick={() => setFiltroConferencia('REVISAR')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Revisar Hoje</p>
                  <p className="text-3xl font-bold text-amber-600 mt-2">{stats.revisar}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              {filtroConferencia === 'REVISAR' && (
                <p className="text-xs text-amber-600 mt-2">✓ Filtro ativo</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Busca Rápida */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search size={20} style={{ color: colors.primaza.primary }} />
              Busca Rápida
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-3">
              <Input
                placeholder="Buscar por pedido, cliente, vendedor ou transportadora..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="flex-1"
              />
              <select
                value={filtroTransportadora}
                onChange={(e) => setFiltroTransportadora(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md min-w-[200px]"
                style={{ borderColor: colors.primaza.primary }}
              >
                <option value="TODAS">🚚 Todas Transportadoras</option>
                {transportadoras
                  .filter(t => t.ativa)
                  .sort((a, b) => a.nome.localeCompare(b.nome))
                  .map(transp => (
                    <option key={transp.id} value={transp.nome}>
                      {transp.nome}
                    </option>
                  ))}
              </select>
              {(busca || filtroTransportadora !== 'TODAS') && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setBusca('');
                    setFiltroTransportadora('TODAS');
                  }}
                >
                  Limpar Filtros
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {pedidosFiltrados.length} pedidos
            </p>
          </CardContent>
        </Card>

        {/* Tabela de Pedidos */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Pedidos para Conferência</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.primaza.primary }} />
              </div>
            ) : pedidosFiltrados.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {filtroConferencia === 'CONFERIDOS' 
                  ? 'Nenhum pedido conferido hoje ainda' 
                  : filtroConferencia === 'PENDENTES'
                    ? '🎉 Todos os pedidos foram conferidos!'
                    : filtroConferencia === 'REVISAR'
                      ? '✅ Nenhum pedido para revisar agora'
                      : 'Nenhum pedido encontrado'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2">
                    <tr>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Conferir
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Pedido
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Vendedor
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Transportadora
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Rastreamento
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Atualizar Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Dias
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pedidosPaginaAtual.map((pedido) => {
                      const dias = calcularDiasParados(pedido.created_at);
                      const salvando = salvandoId === pedido.id;
                      const conferindo = conferindoId === pedido.id;
                      const conferido = isConferidoHoje(pedido);
                      const revisar = isRevisarHoje(pedido);

                      return (
                        <tr 
                          key={pedido.id} 
                          className={`hover:bg-gray-50 transition-colors ${
                            revisar ? 'bg-amber-50' : conferido ? 'bg-green-50' : ''
                          }`}
                        >
                          {/* Checkbox de Conferência */}
                          <td className="px-4 py-3 text-center">
                            {conferindo ? (
                              <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                            ) : (
                              <button
                                onClick={() => conferido ? handleDesconferir(pedido.id) : handleConferir(pedido.id, false)}
                                className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                                  revisar
                                    ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                                    : conferido 
                                      ? 'bg-green-500 border-green-500 text-white hover:bg-green-600' 
                                      : 'border-gray-300 hover:border-green-500'
                                }`}
                                title={conferido ? 'Clique para desmarcar' : 'Clique para conferir'}
                              >
                                {(conferido || revisar) && <Check size={14} />}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3 font-medium">{pedido.pedido || '-'}</td>
                          <td className="px-4 py-3 text-sm">{pedido.nome_cliente || '-'}</td>
                          <td className="px-4 py-3 text-sm">{pedido.vendedor || '-'}</td>
                          <td className="px-4 py-3 text-sm">{pedido.transportadora || '-'}</td>
                          <td className="px-4 py-3">
                            {pedido.cod_rastreamento && /^[A-Z]{2}/i.test(pedido.cod_rastreamento) ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600">{pedido.cod_rastreamento}</span>
                                <button
                                  onClick={() => buscarRastreamento(pedido)}
                                  className="text-green-600 hover:text-green-800 transition-colors"
                                  title="Ver rastreamento Correios"
                                >
                                  <Truck size={18} />
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">{pedido.cod_rastreamento || '-'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge 
                              className="border-2"
                              style={getStatusStyle(pedido.status_pedido)}
                            >
                              {pedido.status_pedido || '-'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {salvando ? (
                              <div className="flex items-center text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Salvando...
                              </div>
                            ) : (
                              <select
                                defaultValue=""
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleStatusChange(pedido.id, e.target.value);
                                  }
                                }}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                                style={{ borderColor: colors.primaza.primary }}
                              >
                                <option value="" disabled>Selecione...</option>
                                {statusList.map(status => (
                                  <option key={status.id} value={status.nome}>{status.nome}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`border ${getCorDias(dias)} flex items-center gap-1`}>
                              <Clock size={12} />
                              {dias}d
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => abrirDetalhes(pedido)}
                              style={{ borderColor: colors.primaza.primary, color: colors.primaza.primary }}
                            >
                              <Eye size={14} className="mr-1" />
                              Detalhes
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
          
          {/* Paginação */}
          {pedidosFiltrados.length > itensPorPagina && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Mostrando <strong>{indexPrimeiro + 1}</strong> a <strong>{Math.min(indexUltimo, pedidosFiltrados.length)}</strong> de{' '}
                  <strong>{pedidosFiltrados.length}</strong> pedidos
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => irParaPagina(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                    variant="outline"
                    size="sm"
                  >
                    Anterior
                  </Button>
                  
                  <span className="text-sm text-gray-700 px-4">
                    Página <strong>{paginaAtual}</strong> de <strong>{totalPaginas}</strong>
                  </span>
                  
                  <Button
                    onClick={() => irParaPagina(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                    variant="outline"
                    size="sm"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Modal de Confirmação de Entrega */}
      <ModalConfirmacaoEntrega
        isOpen={modalEntrega.isOpen}
        pedido={modalEntrega.pedido}
        onClose={() => setModalEntrega({ isOpen: false, pedido: null })}
        onSuccess={() => {
          loadPedidos();
          setModalEntrega({ isOpen: false, pedido: null });
        }}
      />

      {/* Modal Pergunta Revisar Hoje */}
      <Dialog open={modalRevisar.isOpen} onOpenChange={(open) => !open && setModalRevisar({ isOpen: false, pedidoId: null, novoStatus: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Revisar novamente hoje?
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600">
              O status será alterado para <strong>{modalRevisar.novoStatus}</strong>.
            </p>
            <p className="text-gray-600 mt-2">
              Deseja revisar este pedido novamente hoje (à tarde)?
            </p>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleConfirmarRevisao(false)}
              className="flex-1"
            >
              Não, só conferir
            </Button>
            <Button
              onClick={() => handleConfirmarRevisao(true)}
              className="flex-1"
              style={{ backgroundColor: colors.primaza.primary }}
            >
              Sim, revisar depois
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes do Pedido */}
      <Dialog open={modalDetalhes.isOpen} onOpenChange={(open) => !open && fecharDetalhes()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="text-xl font-bold" style={{ color: colors.primaza.secondary }}>
                📋 Detalhes do Pedido #{modalDetalhes.pedido?.pedido || '-'}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {modalDetalhes.pedido && (
            <div className="space-y-3">
              {/* Botão Copiar Tudo */}
              <Button
                onClick={() => copiarTudo(modalDetalhes.pedido)}
                className="w-full"
                style={{ backgroundColor: colors.primaza.primary }}
              >
                {copiado === 'tudo' ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" />
                    Copiar Todas Informações
                  </>
                )}
              </Button>

              {/* Campos Copiáveis */}
              <div className="space-y-2 bg-gray-50 rounded-lg p-4">
                {[
                  { label: 'Pedido', campo: 'pedido', valor: modalDetalhes.pedido.pedido },
                  { label: 'Cliente', campo: 'cliente', valor: modalDetalhes.pedido.nome_cliente },
                  { label: 'Vendedor', campo: 'vendedor', valor: modalDetalhes.pedido.vendedor },
                  { label: 'Transportadora', campo: 'transportadora', valor: modalDetalhes.pedido.transportadora },
                  { label: 'Status', campo: 'status', valor: modalDetalhes.pedido.status_pedido },
                  { label: 'NF-e', campo: 'nfe', valor: modalDetalhes.pedido.nfe },
                  { label: 'Cód. Rastreamento', campo: 'rastreamento', valor: modalDetalhes.pedido.cod_rastreamento },
                  { label: 'Previsão Entrega', campo: 'previsao', valor: formatDateBR(modalDetalhes.pedido.prev_entrega) },
                ].map(({ label, campo, valor }) => (
                  <div 
                    key={campo}
                    className="flex items-center justify-between bg-white p-3 rounded border hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => copiarCampo(campo, valor)}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-gray-500 uppercase">{label}</span>
                      <p className="font-medium text-gray-900 truncate">{valor || '-'}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="flex-shrink-0 ml-2">
                      {copiado === campo ? (
                        <CheckCircle size={16} className="text-green-600" />
                      ) : (
                        <Copy size={16} className="text-gray-400" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>

              {/* Observações se houver */}
              {modalDetalhes.pedido.observacao && (
                <div 
                  className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors"
                  onClick={() => copiarCampo('obs', modalDetalhes.pedido.observacao)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-yellow-700 uppercase font-medium">Observação</span>
                    {copiado === 'obs' ? (
                      <CheckCircle size={16} className="text-green-600" />
                    ) : (
                      <Copy size={16} className="text-yellow-600" />
                    )}
                  </div>
                  <p className="text-sm text-yellow-800 mt-1">{modalDetalhes.pedido.observacao}</p>
                </div>
              )}

              <p className="text-xs text-gray-500 text-center mt-2">
                💡 Clique em qualquer campo para copiar
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Rastreamento Correios */}
      {rastreamentoModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
              <div className="flex items-start justify-between text-white">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-white/20 p-2 rounded-lg">
                      <Truck size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">Rastreamento Correios</h3>
                      <p className="text-sm text-blue-100 mt-0.5">
                        Pedido #{rastreamentoModal.pedido?.pedido}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium">
                      {rastreamentoModal.pedido?.cod_rastreamento}
                    </span>
                    {rastreamentoModal.pedido?.prev_entrega && !rastreamentoModal.dados?.entregue && (
                      <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Clock size={12} />
                        Previsão: {formatDateBR(rastreamentoModal.pedido.prev_entrega)}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setRastreamentoModal({ isOpen: false, pedido: null, dados: null, loading: false })}
                  className="text-white/90 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {rastreamentoModal.loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-16 w-16 animate-spin text-blue-500 mb-4" />
                  <p className="text-gray-600 font-medium">Buscando informações nos Correios...</p>
                </div>
              ) : rastreamentoModal.dados?.erro ? (
                <Alert variant="destructive">
                  <AlertDescription>{rastreamentoModal.dados.erro}</AlertDescription>
                </Alert>
              ) : rastreamentoModal.dados ? (
                <div className="space-y-6">
                  <div className={`rounded-xl p-5 border-2 ${rastreamentoModal.dados.entregue ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'}`}>
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${rastreamentoModal.dados.entregue ? 'bg-green-100' : 'bg-blue-100'}`}>
                        {rastreamentoModal.dados.entregue ? (
                          <CheckCircle className="h-8 w-8 text-green-600" />
                        ) : (
                          <Package className="h-8 w-8 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-bold text-lg ${rastreamentoModal.dados.entregue ? 'text-green-800' : 'text-blue-800'}`}>
                          {rastreamentoModal.dados.ultimo_status || 'Sem informações'}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-600">
                          {rastreamentoModal.dados.ultima_data && (
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDateBR(rastreamentoModal.dados.ultima_data)}
                            </span>
                          )}
                          {rastreamentoModal.dados.ultima_localizacao && (
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {rastreamentoModal.dados.ultima_localizacao}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const sugestao = devesugerirAtualizacao();
                    if (!sugestao) return null;
                    
                    return (
                      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-5">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 bg-amber-100 p-2 rounded-lg">
                            <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-amber-900 mb-2 text-lg">
                              💡 Sugestão de Atualização
                            </h4>
                            <p className="text-amber-800 mb-2">
                              {sugestao.mensagem}
                            </p>
                            <p className="text-sm text-amber-700 mb-4 bg-white/50 px-3 py-2 rounded-lg">
                              Status atual no sistema: <span className="font-semibold">{rastreamentoModal.pedido?.status_pedido || 'Não definido'}</span>
                            </p>
                            <Button
                              className="bg-amber-600 hover:bg-amber-700 text-white font-medium shadow-md"
                              onClick={abrirModalConfirmacaoEntrega}
                              size="sm"
                            >
                              <CheckCircle size={16} className="mr-2" />
                              Confirmar Entrega ({sugestao.statusSugerido})
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {rastreamentoModal.dados.eventos && rastreamentoModal.dados.eventos.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <Clock size={18} className="text-blue-600" />
                        </div>
                        <h4 className="font-bold text-gray-800 text-lg">
                          Histórico de Movimentação
                        </h4>
                        <span className="text-sm text-gray-500 ml-auto">
                          {rastreamentoModal.dados.eventos.length} eventos
                        </span>
                      </div>
                      
                      <div className="relative">
                        <div className="absolute left-[21px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-yellow-400 via-yellow-500 to-yellow-400"></div>
                        
                        <div className="space-y-4">
                          {rastreamentoModal.dados.eventos.map((evento, index) => (
                            <div key={index} className="relative pl-14">
                              <div className={`absolute left-0 top-0 p-2.5 rounded-xl ${
                                index === 0 ? 'bg-blue-100 ring-4 ring-blue-50' : 'bg-yellow-50 ring-2 ring-yellow-100'
                              }`}>
                                {index === 0 ? (
                                  <Package size={18} className="text-blue-600" />
                                ) : evento.descricao.toLowerCase().includes('entrega') ? (
                                  <Truck size={18} className="text-yellow-600" />
                                ) : (
                                  <MapPin size={18} className="text-yellow-600" />
                                )}
                              </div>
                              
                              <div className={`rounded-xl p-4 ${
                                index === 0 
                                  ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-sm' 
                                  : 'bg-white border border-gray-200 hover:border-gray-300 transition-colors'
                              }`}>
                                <p className={`font-semibold mb-2 ${index === 0 ? 'text-blue-900' : 'text-gray-800'}`}>
                                  {evento.descricao}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                                  {evento.data && (
                                    <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                                      <Clock size={14} className="text-gray-500" />
                                      <span className="font-medium">{formatDateBR(evento.data)}</span>
                                    </span>
                                  )}
                                  {evento.localizacao && (
                                    <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                                      <MapPin size={14} className="text-gray-500" />
                                      <span className="font-medium">{evento.localizacao}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-center gap-2 pt-4 text-xs text-gray-400 border-t">
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-medium">
                      Fonte: Correios API Oficial
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="border-t bg-gray-50 px-6 py-4 flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => buscarRastreamento(rastreamentoModal.pedido)}
                  disabled={rastreamentoModal.loading}
                  className="font-medium"
                >
                  <RefreshCw size={16} className={`mr-2 ${rastreamentoModal.loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
                <Button
                  variant="outline"
                  onClick={copiarLinkPublico}
                  className={`font-medium transition-all ${linkCopiado ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
                >
                  {linkCopiado ? (
                    <>
                      <CheckCircle size={16} className="mr-2" />
                      Link Copiado!
                    </>
                  ) : (
                    <>
                      <Clipboard size={16} className="mr-2" />
                      Compartilhar
                    </>
                  )}
                </Button>
              </div>
              <Button
                onClick={() => setRastreamentoModal({ isOpen: false, pedido: null, dados: null, loading: false })}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenciaDiaria;
